import {
  FieldHeaderMapping,
  FieldHeadersMappingsMap,
  PreviousFieldMappings,
  StringToBooleanMap,
} from '@/app/features/products/common/components/frontend-processing/models';
import { HeaderInfo, InputLabelsMap } from '@ssar-app/domain';

export const toFieldMappingsMapFromMap = (
  fieldHeadersMap: PreviousFieldMappings[],
): FieldHeadersMappingsMap[] => {
  return fieldHeadersMap.reduce((acc, curr) => {
    const recordIndex = acc.findIndex(
      (a) => a.field === curr.field.toLowerCase(),
    );
    const toInsert: FieldHeadersMappingsMap = {
      field: curr.field.toLowerCase(),
      fieldHeaderMapping: {
        experianHeader: curr.previousHeader,
        clientHeader: curr.field,
      },
    };
    if (recordIndex === -1) {
      acc.push(toInsert);
    } else {
      acc[recordIndex] = toInsert;
    }
    return acc;
  }, [] as FieldHeadersMappingsMap[]);
};

const getInputLayoutFields = (inputLabelsMap: InputLabelsMap): string[] =>
  Object.values(inputLabelsMap).map((a) => a.name);

export const updateFieldMappings = (
  currentFieldMappingsMap: FieldHeadersMappingsMap[],
  newFieldMapping: FieldHeaderMapping,
  inputLabelsMap: InputLabelsMap,
  hasHeaders: boolean,
  columnIndex: number,
): {
  fieldHeaderMappingsMap: FieldHeadersMappingsMap[];
  headerSelectionStatuses: StringToBooleanMap[];
} => {
  const newKey = hasHeaders
    ? newFieldMapping.clientHeader
    : columnIndex.toString();
  // const newMapping = currentFieldMappingsMap[newKey];
  const recordIndex = currentFieldMappingsMap.findIndex(
    (a) => a.field === newKey,
  );
  const toInsert: FieldHeadersMappingsMap = {
    field: newKey,
    fieldHeaderMapping: newFieldMapping,
  };
  if (recordIndex === -1) {
    currentFieldMappingsMap.push(toInsert);
  } else {
    currentFieldMappingsMap[recordIndex] = toInsert;
  }
  return withHeaderSelectionStatus(
    [...currentFieldMappingsMap],
    getInputLayoutFields(inputLabelsMap),
  );
};

const generateInitialFieldMappings = (
  headerInfo: HeaderInfo,
  previousFieldMappingsMap: FieldHeadersMappingsMap[],
  inputLabelsMap: InputLabelsMap,
): {
  fieldHeaderMappingsMap: FieldHeadersMappingsMap[];
  headerSelectionStatuses: StringToBooleanMap[];
} => {
  const defaultInputLayoutFields = getInputLayoutFields(inputLabelsMap);
  const prevClientHeaders = previousFieldMappingsMap.map(
    (prevFieldMap) => prevFieldMap.field,
  );
  const automaticFieldMappings: FieldHeadersMappingsMap[] =
    headerInfo.detectedHeaders.reduce((acc, curr, index) => {
      const alreadyMapped = prevClientHeaders.includes(
        headerInfo.actualHeaders[index].toLowerCase(),
      );
      const defaultExperianHeaders = curr.filter((a) =>
        defaultInputLayoutFields.some(
          (b) => a.toLowerCase() === b.toLowerCase(),
        ),
      );
      const priorFieldMapping = previousFieldMappingsMap.find(
        (a) =>
          a.field ===
          (headerInfo.hasHeaders
            ? headerInfo.actualHeaders[index].toLowerCase()
            : index.toString()),
      )?.fieldHeaderMapping;
      const priorExperianHeader = defaultInputLayoutFields.some(
        (a) =>
          a.toLowerCase() === priorFieldMapping?.experianHeader?.toLowerCase(),
      )
        ? priorFieldMapping?.experianHeader
        : null;
      // console.log(defaultExperianHeaders, priorExperianHeader);
      const mapping: FieldHeaderMapping = {
        experianHeader: !alreadyMapped
          ? priorExperianHeader
            ? defaultExperianHeaders.some(
                (a) => a.toLowerCase() === priorExperianHeader.toLowerCase(),
              )
              ? priorExperianHeader
              : undefined
            : defaultExperianHeaders[0]
          : priorExperianHeader,
        clientHeader: headerInfo.actualHeaders[index],
      };
      const recordIndex = acc.findIndex(
        (a) =>
          a.field ===
          (headerInfo.hasHeaders
            ? headerInfo.actualHeaders[index]
            : index.toString()),
      );
      const toInsert: FieldHeadersMappingsMap = {
        field: headerInfo.hasHeaders
          ? headerInfo.actualHeaders[index]
          : index.toString(),
        fieldHeaderMapping: mapping,
      };
      if (recordIndex === -1) {
        acc.push(toInsert);
      } else {
        acc[recordIndex] = toInsert;
      }
      return acc;
    }, [] as FieldHeadersMappingsMap[]);
  // console.log(newFieldMappingsMap);

  return withHeaderSelectionStatus(
    [...automaticFieldMappings],
    defaultInputLayoutFields,
  );
};

const withHeaderSelectionStatus = (
  fieldHeaderMappingsMap: FieldHeadersMappingsMap[],
  defaultInputLayoutFields: string[],
) => {
  const effectiveFieldSelections = fieldHeaderMappingsMap.map((entry) => {
    return entry.fieldHeaderMapping.experianHeader || '';
  });

  const lowerCasedEffectiveFieldSelections = effectiveFieldSelections.map((a) =>
    a.toLowerCase(),
  );

  const headerSelectionStatuses: StringToBooleanMap[] =
    defaultInputLayoutFields.reduce((acc, curr) => {
      const recordIndex = acc.findIndex((a) => a.header === curr);
      const toInsert: StringToBooleanMap = {
        header: curr,
        status: lowerCasedEffectiveFieldSelections.includes(curr.toLowerCase()),
      };
      if (recordIndex === -1) {
        acc.push(toInsert);
      } else {
        acc[recordIndex] = toInsert;
      }
      return acc;
    }, [] as StringToBooleanMap[]);
  return {
    fieldHeaderMappingsMap,
    headerSelectionStatuses,
  };
};

export { generateInitialFieldMappings };
